/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Autocomplete, Box, Tooltip, Typography } from '@mui/material';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { HiExclamationCircle } from 'react-icons/hi';
import Skeleton from '@mui/material/Skeleton';

import BaseInput from 'components/common/BaseInput';
import HelperDesc from 'components/common/WarningInputMessage/WarningInputMessage';
import LoadingButton from 'components/common/LoadingButton/LoadingButton';

import { RootState, AppDispatch } from 'redux/store';
import { updateApp } from 'redux/application/application.middleware';
import { resetUpdatedApp } from 'redux/application/application.slice';
import { loadApplications } from 'redux/applications/applications-middleware';
import api, { ApplicationPayload, ApplicationStatus } from 'api';

import {
    getUserFriendlyStatus,
    convertBackToEnum,
} from '../../utils/userFriendlyApplicationStatus';
import styles from './UpdateCase.module.scss';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { chosenCountryName, countryNameOptions } from 'utils/countries';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';

interface Case {
    country: string;
    caseStatus: string;
    visaProgram: string;
}

type Props = {
    closeModal: () => void;
};

const UpdateCase = ({ closeModal }: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [, setCaseCountries] = useState<string[]>([]);
    const { codes } = useEnabledCountries();
    const { id, appID } = useParams();

    const { data, error, isLoading } = useSelector((state: RootState) => state.expertiseList);
    const {
        updatedApp,
        newApp,
        entity: candidateApp,
    } = useSelector((state: RootState) => state.application);
    const countryOptions = countryNameOptions(codes, candidateApp?.country);
    const [activeCountry, setActiveCountry] = useState<string | null>(() =>
        chosenCountryName(codes)
    );
    const [countryBasedProgramList, setCountryBasedProgramList] = useState<any[]>([]);
    const dispatch = useDispatch<AppDispatch>();

    // refactor needed
    // useEffect(() => {
    //     dispatch(loadExpertiseList());

    //     return () => {
    //         dispatch(resetUpdatedApp());
    //     };
    // }, []);

    useEffect(() => {
        return () => {
            dispatch(resetUpdatedApp());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (id && activeCountry) {
            getPrograms(id, activeCountry);
        }
    }, [id, activeCountry]);

    const getPrograms = async (userId: any, country: string) => {
        try {
            const resp = await api.programList.get(userId, country);
            if (resp?.status === 200 || resp?.status === 201) {
                // Handle both new enhanced format (objects) and old format (strings)
                const data = resp?.data;
                if (Array.isArray(data) && data.length > 0) {
                    // Check if it's the new enhanced format (objects with program_name)
                    if (typeof data[0] === 'object' && data[0].program_name) {
                        setCountryBasedProgramList(data);
                    } else {
                        // Old format (strings) - convert to new format for consistency
                        setCountryBasedProgramList(
                            data.map((program: string) => ({
                                program_name: program,
                                qualification_status: 'not_qualified',
                                display_color: 'black',
                            }))
                        );
                    }
                } else {
                    setCountryBasedProgramList([]);
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (data) {
            const countries = Object.keys(data);
            setCaseCountries(countries);
        }
    }, [data]);

    const resetForm = () => {
        reset((formValues) => ({
            ...formValues,
            caseStatus: '',
            visaProgram: '',
        }));
    };

    useEffect(() => {
        resetForm();
        // prepareVisaPrograms<string | null>(activeCountry);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCountry]);

    useEffect(() => {
        if (candidateApp) {
            const defaultValues = {
                country: chosenCountryName(codes, candidateApp.country) ?? '',
                caseStatus: getDefaultCaseStatus(),
                visaProgram: candidateApp.program_name ? candidateApp.program_name : '',
            };
            setActiveCountry(defaultValues.country || null);
            reset(() => defaultValues);
            // prepareVisaPrograms<string>(candidateApp.country);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateApp]);

    // const prepareVisaPrograms = <T,>(key: T) => {
    //     if (data && key) {
    //         const currentCountryPrograms: string[] = data[key as keyof typeof data];
    //         const withoutEmptyValue = currentCountryPrograms.filter((program) => program !== '');
    //         const onlyUniqueValues = [...new Set(withoutEmptyValue)];
    //         setVisaPrograms(onlyUniqueValues);
    //         return;
    //     } else if (data && activeCountry) {
    //         const currentCountryPrograms: string[] = data[activeCountry as keyof typeof data];
    //         const withoutEmptyValue = currentCountryPrograms.filter((program) => program !== '');
    //         const onlyUniqueValues = [...new Set(withoutEmptyValue)];
    //         setVisaPrograms(onlyUniqueValues);
    //         return;
    //     }
    //     setVisaPrograms([]);
    // };

    useEffect(() => {
        if (updatedApp.message) {
            setTimeout(() => {
                closeModal();
            }, 1500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedApp, newApp]);

    const getDefaultCaseStatus = useCallback(() => {
        if (candidateApp) {
            if (
                (candidateApp.status === ApplicationStatus.OPEN ||
                    candidateApp.status === ApplicationStatus.CONSULTATION_SCHEDULED ||
                    candidateApp.status === ApplicationStatus.AWAITING_OFFER) &&
                !!candidateApp?.program_name
            ) {
                return ApplicationStatus.AWAITING_OFFER;
            } else {
                return getUserFriendlyStatus(candidateApp.status);
            }
        }
        return '';
    }, [candidateApp]);

    const {
        control,
        reset,
        formState: { errors, isDirty },
        handleSubmit,
    } = useForm<Case>({
        mode: 'all',
        defaultValues: {
            country: chosenCountryName(codes) ?? '',
            caseStatus: '',
            visaProgram: '',
        },
    });

    const submitForm: SubmitHandler<FieldValues> = async (data) => {
        if (!updatedApp.message) {
            await updateCase(data);
            if (candidateApp) {
                dispatch(loadApplications(candidateApp.candidate_id.toString()));
            }
            return;
        }

        closeModal();
    };

    const updateCase = async (data: FieldValues) => {
        if (appID && candidateApp) {
            const payload: ApplicationPayload & { appId: string } = {
                candidate_id: candidateApp.candidate_id,
                consultant_id: candidateApp.consultant_id,
                appId: appID,
                country: data.country,
                status:
                    candidateApp.program_name === null
                        ? candidateApp.status
                        : convertBackToEnum(data.caseStatus),
                program_name: data.visaProgram,
            };
            await dispatch(updateApp(payload));
        }
    };

    const activeBtnCondition = isDirty && Object.keys(errors).length === 0;

    // const isCaseStatusInputVisible = () => {
    //     if (
    //         candidateApp?.program_name === null ||
    //         candidateApp?.status === ApplicationStatus.OPEN ||
    //         candidateApp?.status === ApplicationStatus.CONSULTATION_SCHEDULED ||
    //         candidateApp?.status === ApplicationStatus.AWAITING_OFFER ||
    //         candidateApp?.status === ApplicationStatus.OFFER ||
    //         candidateApp?.status === ApplicationStatus.COUNTEROFFER_CANDIDATE ||
    //         candidateApp?.status === ApplicationStatus.COUNTEROFFER_CONSULTANT ||
    //         candidateApp?.status === ApplicationStatus.OFFER_REJECTED
    //     ) {
    //         return false;
    //     }
    //     return true;
    // };

    // const getStatuses = () => {
    //     const allStatuses = getUserFriendlyStatuses();

    //     if (!candidateApp?.program_name) {
    //         return [...allStatuses.slice(0, 1), ...allStatuses.slice(7)];
    //     }

    //     if (
    //         candidateApp?.status === ApplicationStatus.OPEN ||
    //         candidateApp?.status === ApplicationStatus.CONSULTATION_SCHEDULED ||
    //         candidateApp?.status === ApplicationStatus.AWAITING_OFFER ||
    //         candidateApp?.status === ApplicationStatus.OFFER ||
    //         candidateApp?.status === ApplicationStatus.COUNTEROFFER_CANDIDATE ||
    //         candidateApp?.status === ApplicationStatus.COUNTEROFFER_CONSULTANT ||
    //         candidateApp?.status === ApplicationStatus.OFFER_REJECTED ||
    //         candidateApp?.status === ApplicationStatus.OFFER_ACCEPTED
    //     ) {
    //         return allStatuses;
    //     }
    //     const statusesAfterOFFER_ACCEPTED = allStatuses.slice(7);
    //     return statusesAfterOFFER_ACCEPTED;
    // };

    const tooltipText = (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'green',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">
                    {t('potentiallyQualified', { lng: language })}
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'orange',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">
                    {t('potentiallyQualifiedWithJobOffer', { lng: language })}
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        bgcolor: 'black',
                        borderRadius: '50%',
                    }}
                />
                <Typography variant="body2">{t('allOtherPrograms', { lng: language })}</Typography>
            </Box>
        </Box>
    );

    return (
        <>
            {!error && !updatedApp.error && !newApp.error && (
                <form className={styles.root} onSubmit={handleSubmit(submitForm)}>
                    <section className={styles['title-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <h2 className={styles.title}>
                                    {t('caseProperties', { lng: language })}
                                </h2>
                            </Skeleton>
                        ) : (
                            <h2 className={styles.title}>
                                {t('caseProperties', { lng: language })}
                            </h2>
                        )}
                    </section>

                    <div className={styles['text-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <p className={styles.text}>{t('caseCountry', { lng: language })}</p>
                            </Skeleton>
                        ) : (
                            <p className={styles.text}>{t('caseCountry', { lng: language })}</p>
                        )}
                    </div>

                    <div className={styles['input-wrapper']}>
                        {isLoading ? (
                            <Skeleton height={72} />
                        ) : (
                            <Controller
                                control={control}
                                name={'country'}
                                rules={{ required: t('requiredField', { lng: language }) }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        id="country"
                                        options={countryOptions}
                                        disablePortal
                                        value={value || null}
                                        onBlur={onBlur}
                                        renderOption={(props, option) => (
                                            <li {...props} style={{ margin: 0 }}>
                                                {option}
                                            </li>
                                        )}
                                        onChange={(_, data) => {
                                            onChange(data as string);
                                            setActiveCountry(data);
                                            return data;
                                        }}
                                        renderInput={(params) => (
                                            <BaseInput
                                                {...params}
                                                label={t('country', { lng: language })}
                                                error={error ? true : false}
                                                helperText={
                                                    error ? (
                                                        <HelperDesc
                                                            message={error.message?.toString()}
                                                        />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}
                    </div>

                    {/* {isCaseStatusInputVisible() && (
                        <>
                            <div className={styles['text-wrapper']}>
                                {isLoading ? (
                                    <Skeleton>
                                        <p className={styles.text}>
                                            {t('caseStatus', { lng: language })}
                                        </p>
                                    </Skeleton>
                                ) : (
                                    <p className={styles.text}>
                                        {t('caseStatus', { lng: language })}
                                    </p>
                                )}
                            </div>
                            <div className={styles['input-wrapper']}>
                                {isLoading ? (
                                    <Skeleton height={72} />
                                ) : (
                                    <Controller
                                        control={control}
                                        name={'caseStatus'}
                                        rules={{ required: t('requiredField', { lng: language }) }}
                                        render={({
                                            field: { onChange, onBlur, value },
                                            fieldState: { error },
                                        }) => (
                                            <Autocomplete
                                                className={styles.input}
                                                isOptionEqualToValue={(option, value) =>
                                                    option === value
                                                }
                                                id="case"
                                                value={value}
                                                options={getStatuses()}
                                                disablePortal
                                                onBlur={onBlur}
                                                renderOption={(props, option) => (
                                                    <li {...props} style={{ margin: 0 }}>
                                                        {option}
                                                    </li>
                                                )}
                                                onChange={(_, data) => {
                                                    onChange(data as string);
                                                    return data;
                                                }}
                                                renderInput={(params) => (
                                                    <BaseInput
                                                        {...params}
                                                        label={t('caseStatus', { lng: language })}
                                                        error={error ? true : false}
                                                        helperText={
                                                            error ? (
                                                                <HelperDesc
                                                                    message={error.message?.toString()}
                                                                />
                                                            ) : (
                                                                ''
                                                            )
                                                        }
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                )}
                            </div>
                        </>
                    )} */}

                    <div className={styles['text-wrapper']}>
                        {isLoading ? (
                            <Skeleton>
                                <p className={styles.text}>{t('service', { lng: language })}</p>
                            </Skeleton>
                        ) : (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <p className={styles.text}>{t('service', { lng: language })}</p>

                                <Tooltip title={tooltipText} placement="top" arrow>
                                    <InfoIcon sx={{ cursor: 'pointer', fontSize: 18 }} />
                                </Tooltip>
                            </Box>
                        )}
                    </div>

                    <div className={styles['input-wrapper']}>
                        {isLoading ? (
                            <Skeleton height={72} />
                        ) : (
                            <Controller
                                control={control}
                                name={'visaProgram'}
                                rules={{ required: t('requiredField', { lng: language }) }}
                                render={({
                                    field: { onChange, onBlur, value },
                                    fieldState: { error },
                                }) => (
                                    <Autocomplete
                                        // freeSolo
                                        // disabled={activeCountry ? false : true}
                                        disabled={
                                            !candidateApp?.program_name && !activeCountry
                                                ? true
                                                : false
                                        }
                                        isOptionEqualToValue={(option, value) => option === value}
                                        fullWidth
                                        disablePortal
                                        id="program"
                                        value={value}
                                        options={countryBasedProgramList}
                                        // isOptionEqualToValue={(option, value) => option.programs === value.programs}
                                        getOptionLabel={(option) => option.program_name || option}
                                        onBlur={onBlur}
                                        onChange={(_, data) => {
                                            const programName =
                                                typeof data === 'string'
                                                    ? data
                                                    : data?.program_name;
                                            onChange(programName);
                                            return data;
                                        }}
                                        renderOption={(props, option) => (
                                            <li
                                                {...props}
                                                key={option.program_name || option}
                                                style={{
                                                    margin: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <span>{option.program_name || option}</span>
                                                {option.display_color && (
                                                    <span
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: option.display_color,
                                                            marginLeft: '8px',
                                                        }}
                                                    />
                                                )}
                                            </li>
                                        )}
                                        renderInput={(params: any) => (
                                            <BaseInput
                                                className={styles.input}
                                                {...params}
                                                label={t('service', { lng: language })}
                                                error={error ? true : false}
                                                helperText={
                                                    error ? (
                                                        <HelperDesc
                                                            message={error.message?.toString()}
                                                        />
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        )}
                    </div>

                    <div className={styles.actions}>
                        {isLoading ? (
                            <Skeleton height={44} width={185} />
                        ) : (
                            <LoadingButton
                                isLoading={updatedApp.isLoading || newApp.isLoading}
                                fulfilled={!!updatedApp.message || !!newApp.entity}
                                style={{ paddingLeft: '38px', paddingRight: '38px' }}
                                type="submit"
                                variant="contained"
                                size="min"
                                color="orange"
                                disabled={!activeBtnCondition}
                            >
                                {t('saveProperties', { lng: language })}
                            </LoadingButton>
                        )}
                    </div>
                </form>
            )}

            {(error || updatedApp.error || newApp.error) && (
                <div className={styles.error}>
                    <HiExclamationCircle className={styles['error__icon']} />
                    <h3 className={styles['error__text']}>{t('errorTitle', { lng: language })}</h3>
                    <span className={styles['error__desc']}>
                        {t('somethingWentWrong', { lng: language })}
                    </span>
                </div>
            )}
        </>
    );
};
export default UpdateCase;
